/* @flow */

import classNames from 'classnames';
import * as React from 'react';
import { Link } from 'react-router-dom';
import {
  hasURLProtocol,
  isURLAnchor,
  isURLMailto,
} from '@braindate/util/lib/url';
import createUseThemeStyles from 'src/shared/app/base/util/createUseThemeStyles';
import styles from './BaseLink.style';

type Props = {
  children: React.Node,
  href?: string | null,
  state?: Object,
  className?: ?string,
  onClick?: ?(e: Event) => void,
  handleMouseOver?: () => void,
  newWindow?: boolean,
  'aria-haspopup'?: ?string | boolean,
};

const useStyles = createUseThemeStyles(styles);

const BaseLink = ({
  children,
  href,
  state,
  className,
  onClick,
  handleMouseOver,
  newWindow,
  'aria-haspopup': ariaHaspopup,
}: Props): React.Node => {
  const useAnchorTag =
    href &&
    typeof href === 'string' &&
    (hasURLProtocol(href) || isURLAnchor(href) || isURLMailto(href));
  const useButtonTag = !href;
  const classes = useStyles();

  /*
   |----------------------------------------------------------------------------
   | Event Handlers
   |----------------------------------------------------------------------------
   */

  const handleClick = (e: MouseEvent) => {
    const { currentTarget } = e;

    if (
      currentTarget instanceof Element &&
      currentTarget.getAttribute('href') === '#'
    ) {
      e.preventDefault();
    }

    if (onClick) {
      onClick(e);
    }
  };

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */

  const props = {
    className,
    children,
    onClick: onClick ? handleClick : null,
    onMouseOver: handleMouseOver || null,
    href: undefined,
    role: undefined,
    'aria-haspopup': ariaHaspopup,
    state,
  };

  if (useAnchorTag) {
    props.href = href || '#';
  }

  if (useAnchorTag) {
    return <a {...props} target={newWindow ? '_blank' : null} />;
  } else if (useButtonTag) {
    return (
      <button
        {...props}
        className={classNames([classes.button, className])}
      ></button>
    );
  }

  // $FlowIssue
  return <Link {...props} to={href} />;
};

export default React.memo<Props>(BaseLink);
